import React, { useState, useEffect } from 'react';
import { Settings, Loader2, CheckCircle, XCircle, Upload, Database } from 'lucide-react';
import { settingsAPI, seedAPI } from '../../services/api';
import toast from 'react-hot-toast';

const AI_MODELS = [
  { value:'llama-3.3-70b-versatile', label:'LLaMA 3.3 70B (default, free)', provider:'groq' },
  { value:'llama-3.1-8b-instant',    label:'LLaMA 3.1 8B (fast, free)',     provider:'groq' },
  { value:'mixtral-8x7b-32768',      label:'Mixtral 8x7B (free)',            provider:'groq' },
  { value:'gemma2-9b-it',            label:'Gemma2 9B (free)',               provider:'groq' },
  { value:'claude-sonnet-4-6',       label:'Claude Sonnet 4.6 (premium)',    provider:'anthropic' },
  { value:'gpt-4o',                  label:'GPT-4o (premium)',               provider:'openai' },
];

const F = ({ label, children, hint }) => (
  <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [health, setHealth]     = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [activeSection, setActiveSection] = useState('branding');

  useEffect(() => {
    settingsAPI.all().then(({ data }) => {
      const map = {};
      (data.data||[]).forEach(s=>{ map[s.setting_key]=s.setting_value; });
      setSettings(map);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const checkHealth = async () => {
    setHealthLoading(true);
    try { const { data } = await settingsAPI.health(); setHealth(data.data); }
    catch {} finally { setHealthLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved');
      if (logoFile) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        await settingsAPI.uploadLogo(fd);
        toast.success('Logo updated');
        setLogoFile(null);
      }
    } catch {} finally { setSaving(false); }
  };

  const set = (k) => (e) => setSettings(s=>({...s,[k]:e.target.value}));

  const selectedModel = AI_MODELS.find(m=>m.value===settings.ai_model);
  const needsApiKey = selectedModel?.provider !== 'groq';

  const SECTIONS = [
    { id:'branding',      label:'Branding'       },
    { id:'localisation',  label:'Localisation'   },
    { id:'ai',            label:'AI Engine'      },
    { id:'email',         label:'Email / SMTP'   },
    { id:'health',        label:'System Health'  },
  { id:'demo',          label:'Demo Data'      },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Settings & Administration</h1>
          <p className="text-sm text-gray-500">White-label configuration, AI engine, system health</p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
          {saving&&<Loader2 size={14} className="animate-spin"/>} Save All Settings
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Sidebar nav */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 h-fit">
          {SECTIONS.map(s=>(
            <button key={s.id} onClick={()=>setActiveSection(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-0.5 ${activeSection===s.id?'bg-primary-500 text-white':'text-gray-600 hover:bg-gray-50'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-6 space-y-5">

          {/* Branding */}
          {activeSection==='branding' && <>
            <h2 className="text-base font-semibold text-gray-800 pb-2 border-b border-gray-100">White-Label Branding</h2>
            <F label="Application Name">
              <I value={settings.app_name||''} onChange={set('app_name')} placeholder="EnergyOS ERP 360"/>
            </F>
            <F label="Company Name">
              <I value={settings.company_name||''} onChange={set('company_name')} placeholder="Your Company Name"/>
            </F>
            <F label="Primary Colour" hint="Used for buttons, links, and active states">
              <div className="flex gap-3 items-center">
                <I type="color" value={settings.primary_color||'#1D6B4E'} onChange={set('primary_color')} className="w-16 h-10 p-1 rounded cursor-pointer"/>
                <I value={settings.primary_color||'#1D6B4E'} onChange={set('primary_color')} placeholder="#1D6B4E" className="flex-1"/>
              </div>
            </F>
            <F label="Company Logo">
              <div className="flex items-center gap-4">
                {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="h-12 w-12 object-contain rounded-lg border border-gray-200"/>}
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
                  <Upload size={14}/> {logoFile?logoFile.name:'Upload Logo'}
                  <input type="file" accept=".png,.jpg,.jpeg,.svg" className="hidden" onChange={e=>setLogoFile(e.target.files[0])}/>
                </label>
              </div>
            </F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Admin Email"><I type="email" value={settings.admin_email||''} onChange={set('admin_email')}/></F>
              <F label="Support Email"><I type="email" value={settings.support_email||''} onChange={set('support_email')}/></F>
            </div>
          </>}

          {/* Localisation */}
          {activeSection==='localisation' && <>
            <h2 className="text-base font-semibold text-gray-800 pb-2 border-b border-gray-100">Localisation</h2>
            <div className="grid grid-cols-2 gap-4">
              <F label="Currency">
                <select value={settings.currency||'EUR'} onChange={set('currency')} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">
                  {['EUR','GBP','USD','CHF','SEK','NOK','DKK'].map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </F>
              <F label="Language">
                <select value={settings.language||'en'} onChange={set('language')} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none bg-white">
                  <option value="en">English</option>
                  <option value="de">German (coming soon)</option>
                </select>
              </F>
              <F label="Timezone">
                <select value={settings.timezone||'Europe/Berlin'} onChange={set('timezone')} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none bg-white">
                  {['Europe/Berlin','Europe/London','Europe/Amsterdam','Europe/Paris','Europe/Madrid','UTC'].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </F>
              <F label="Session Timeout (minutes)"><I type="number" value={settings.session_timeout||'480'} onChange={set('session_timeout')}/></F>
            </div>
          </>}

          {/* AI Engine */}
          {activeSection==='ai' && <>
            <h2 className="text-base font-semibold text-gray-800 pb-2 border-b border-gray-100">AI Engine Configuration</h2>
            <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 text-sm text-primary-800">
              Switch models anytime — all AI calls are proxied through the backend. Groq models are free. Claude and GPT-4o require your own API key.
            </div>
            <F label="AI Model">
              <select value={settings.ai_model||'llama-3.3-70b-versatile'} onChange={set('ai_model')} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none bg-white">
                {AI_MODELS.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </F>
            <F label="Base URL" hint="Groq: https://api.groq.com/openai/v1 | Anthropic: https://api.anthropic.com/v1 | OpenAI: https://api.openai.com/v1">
              <I value={settings.ai_base_url||''} onChange={set('ai_base_url')} placeholder="https://api.groq.com/openai/v1"/>
            </F>
            <F label="API Key" hint={needsApiKey?`Required for ${selectedModel?.provider}`:'Optional for Groq free tier'}>
              <I type="password" value={settings.ai_api_key||''} onChange={set('ai_api_key')} placeholder={needsApiKey?'Required':'Optional for Groq'}/>
            </F>
          </>}

          {/* Email */}
          {activeSection==='email' && <>
            <h2 className="text-base font-semibold text-gray-800 pb-2 border-b border-gray-100">Email / SMTP Configuration</h2>
            <p className="text-sm text-gray-500">Used for regulatory deadline alerts and system notifications.</p>
            <div className="grid grid-cols-2 gap-4">
              <F label="SMTP Host"><I value={settings.smtp_host||''} onChange={set('smtp_host')} placeholder="smtp.yourdomain.com"/></F>
              <F label="SMTP Port"><I type="number" value={settings.smtp_port||'587'} onChange={set('smtp_port')}/></F>
              <F label="SMTP User"><I value={settings.smtp_user||''} onChange={set('smtp_user')}/></F>
              <F label="SMTP Password"><I type="password" value={settings.smtp_pass||''} onChange={set('smtp_pass')}/></F>
              <div className="col-span-2"><F label="From Address"><I value={settings.smtp_from||''} onChange={set('smtp_from')} placeholder="EnergyOS <alerts@yourdomain.com>"/></F></div>
            </div>
          </>}

          {/* Health */}
          {activeSection==='health' && <>
            <h2 className="text-base font-semibold text-gray-800 pb-2 border-b border-gray-100">System Health</h2>
            <button onClick={checkHealth} disabled={healthLoading}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
              {healthLoading&&<Loader2 size={14} className="animate-spin"/>} Run Health Check
            </button>
            {health && (
              <div className="space-y-3 mt-4">
                {[['Database', health.database==='connected'],['AI Configured', health.ai_configured]].map(([l,ok])=>(
                  <div key={l} className="flex items-center justify-between py-3 border-b border-gray-50">
                    <span className="text-sm font-medium text-gray-700">{l}</span>
                    {ok ? <span className="flex items-center gap-1.5 text-sm text-emerald-700 font-medium"><CheckCircle size={15}/>OK</span>
                        : <span className="flex items-center gap-1.5 text-sm text-danger font-medium"><XCircle size={15}/>Not configured</span>}
                  </div>
                ))}
                <p className="text-xs text-gray-400">Checked at {new Date(health.timestamp).toLocaleTimeString()}</p>
              </div>
            )}
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 mb-2"><Database size={14} className="text-gray-500"/><h3 className="text-sm font-semibold text-gray-700">Database Backups</h3></div>
              <p className="text-xs text-gray-500 leading-relaxed">Run backups from the host server via cron to avoid Docker socket security risks:</p>
              <pre className="mt-2 text-xs bg-gray-800 text-green-400 p-3 rounded-lg overflow-x-auto">
                {`0 2 * * * docker exec webapps_mysql mysqldump \\
  -u energyos_user -pPASSWORD energyos_erp \\
  > /var/backups/energyos/backup_$(date +%F).sql`}
              </pre>
            </div>
          </>}
        {/* Demo Data */}
          {activeSection==='demo' && <>
            <h2 className="text-base font-semibold text-gray-800 pb-2 border-b border-gray-100">Demo Data Management</h2>
            <DemoDataSection/>
          </>}
        </div>
      </div>
    </div>
  );
}

function DemoDataSection() {
  const [loading, setLoading] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);

  const loadDemo = async () => {
    if (!window.confirm('This will load 7 sample assets, 30 days of production data, PPAs, filings, and more. Continue?')) return;
    setLoading(true);
    try {
      await seedAPI.loadDemo();
      toast.success('Demo data loaded — refresh the dashboard to see it live!');
    } catch {} finally { setLoading(false); }
  };

  const clearDemo = async () => {
    if (!window.confirm('This will permanently delete ALL data including any data you have entered. Continue?')) return;
    setClearing(true);
    try {
      await seedAPI.clearDemo();
      toast.success('All data cleared');
    } catch {} finally { setClearing(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        Use demo data to explore all platform features before entering your real data. Load a full portfolio of 7 assets (solar, wind, hydro, battery, grid) with 30 days of production logs, PPA contracts, regulatory filings, ESG metrics, and maintenance work orders.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-1">Load Demo Data</h3>
          <p className="text-sm text-gray-500 mb-4">Populate all modules with realistic sample data for a 75 MW renewable energy portfolio.</p>
          <button onClick={loadDemo} disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin"/>}
            {loading ? 'Loading…' : 'Load Demo Data'}
          </button>
        </div>
        <div className="bg-white border border-red-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-1">Clear All Data</h3>
          <p className="text-sm text-gray-500 mb-4">Remove all assets, production logs, PPAs, filings, and metrics. Irreversible — use with care.</p>
          <button onClick={clearDemo} disabled={clearing}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-danger text-white text-sm font-medium rounded-lg hover:bg-red-600 disabled:opacity-60">
            {clearing && <Loader2 size={14} className="animate-spin"/>}
            {clearing ? 'Clearing…' : 'Clear All Data'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add demo data section - append to SECTIONS array and content
